/**
 * components/clients/UndoMergeButton.tsx
 *
 * Button with remaining-days indicator and confirmation modal for undoing a merge.
 * Only rendered when can_undo_until is in the future.
 * Created Apr 2026.
 */

import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { ConfirmModal } from '../ui/ConfirmModal';

interface UndoMergeButtonProps {
  mergeId: string;
  canUndoUntil: string;
  onUndo: (mergeId: string) => Promise<boolean>;
  undoing: boolean;
}

export default function UndoMergeButton({
  mergeId,
  canUndoUntil,
  onUndo,
  undoing,
}: UndoMergeButtonProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  const now = new Date();
  const deadline = new Date(canUndoUntil);
  if (deadline <= now) return null;

  const daysLeft = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  const handleConfirm = async () => {
    const ok = await onUndo(mergeId);
    if (ok) setShowConfirm(false);
  };

  return (
    <>
      <button
        onClick={() => setShowConfirm(true)}
        disabled={undoing}
        className="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-800 border border-amber-200 rounded-lg px-2.5 py-1 bg-amber-50 hover:bg-amber-100 transition-colors disabled:opacity-50"
      >
        <RotateCcw className="w-3 h-3" />
        <span>Annuler la fusion</span>
        <span className="text-amber-400">({daysLeft}j restants)</span>
      </button>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        title="Annuler la fusion"
        message="Les deux fiches clients seront restaurées à leur état d'origine et les formulaires seront réassignés. Cette action est irréversible."
        confirmLabel="Annuler la fusion"
        cancelLabel="Garder la fusion"
        variant="default"
      />
    </>
  );
}
